import { Rijtijd, TvvBesteldeRoute, usePatchTvvBestelling } from "@/entities/tvv";
import {
    Badge,
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
    FormErrorMessage,
    FormFieldInput,
    Icon,
    SelectFormField,
} from "@/shared/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { EditBestellingData, editBestellingSchema } from "./schema";
import { useBestellingFormHandlers } from "./use-edit-bestelling-form-handlers";

type EditRouteDialogProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    route: TvvBesteldeRoute;
};

export function BewerkBestellingDialog({ open, setOpen, route }: EditRouteDialogProps) {
    const editTvvBestellingMutation = usePatchTvvBestelling();

    const form = useForm<EditBestellingData>({
        resolver: zodResolver(editBestellingSchema),
        defaultValues: {
            reisperiode: route.reisperiode,
            busFrequentie: route.busFrequentiePerUur,
            aantalBussen: route.bussenBesteld,
            aantalBussenOpBeginStation: route.aantalBussenOpBeginStation,
            isAangepast: false,
        },
    });

    const { handleReisperiodeChange, handleFrequentieChange, handleAantalBussenChange } = useBestellingFormHandlers({ form, route });

    const reisperiodeOptions =
        route.standaardRoute?.periodeFactoren.map((pf) => ({
            label: pf.periode,
            value: pf.periode,
        })) ?? [];
    const heeftReisperiodeOpties = reisperiodeOptions.length > 0;

    const isAangepast = form.watch("isAangepast");
    const errors = form.formState.errors;

    const onSubmit = ({ aantalBussen, busFrequentie, reisperiode, aantalBussenOpBeginStation }: EditBestellingData) => {
        const { id: tvvId, dossierId } = route;
        const payload = { bussenBesteld: aantalBussen, busFrequentie, reisperiode, aantalBussenOpBeginStation };
        editTvvBestellingMutation.mutate(
            { dossierId: String(dossierId), tvvId, ...payload },
            {
                onSuccess: () => {
                    setOpen(false);
                },
            }
        );
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContainer>
                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                        <DialogHeader>
                            <DialogTitle>Busbestelling aanpassen</DialogTitle>
                        </DialogHeader>
                        <DialogBody className="grid gap-y-2">
                            {editTvvBestellingMutation.error && <ErrorDisplay error={editTvvBestellingMutation.error} />}
                            <div>
                                <Badge variant="blue-subtle">
                                    Rijtijd {Rijtijd.formatRijtijd(Rijtijd.getTotaleRijtijd(route.rijtijden))}
                                </Badge>
                            </div>

                            {heeftReisperiodeOpties && (
                                <SelectFormField
                                    form={form}
                                    name="reisperiode"
                                    label="Vul op basis van:"
                                    labelClassName="text-content-subtle font-normal"
                                    options={reisperiodeOptions}
                                    placeholder="Selecteer periode"
                                    onValueChange={handleReisperiodeChange}
                                    layout="inline"
                                    suffix={isAangepast && <span className="text-content-subtle text-s italic">(aangepast)</span>}
                                    className="flex-1"
                                />
                            )}

                            <div className="flex items-end gap-4">
                                <FormFieldInput
                                    form={form}
                                    name="busFrequentie"
                                    label="Busfrequentie"
                                    labelClassName="text-content-subtle"
                                    type="number"
                                    min={1}
                                    max={50}
                                    onValueChange={(value) => handleFrequentieChange(value as number)}
                                    className="w-28"
                                />
                                <div className="mb-2 flex">
                                    <Icon name="swap-horizontal" size="m" />
                                </div>
                                <FormFieldInput
                                    form={form}
                                    name="aantalBussen"
                                    label="Aantal bussen"
                                    labelClassName="text-content-subtle"
                                    type="number"
                                    min={1}
                                    max={100}
                                    onValueChange={(value) => handleAantalBussenChange(value as number)}
                                    className="w-28"
                                />
                            </div>
                            {errors.form && <FormErrorMessage>{errors.form.message}</FormErrorMessage>}
                        </DialogBody>

                        <DialogFooter>
                            <Button type="button" variant="tertiary" onClick={() => setOpen(false)}>
                                Annuleren
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={editTvvBestellingMutation.isPending || !form.formState.isDirty}
                            >
                                Opslaan
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}
